import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { setApiRunEnabled, updateFlow } from '../api/flows';
import { getTags } from '../api/tags';
import { FlowResponse } from '../types';
import { Chip } from './Chip';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface Props {
  flow: FlowResponse;
  onClose: () => void;
}

export function FlowSettingsModal({ flow, onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState(flow.name);
  const [description, setDescription] = useState(flow.description ?? '');
  const [tags, setTags] = useState<string[]>([...flow.tags]);
  const [tagInput, setTagInput] = useState('');
  const [labelTemplate, setLabelTemplate] = useState(flow.labelTemplate ?? '');
  const [error, setError] = useState<string | null>(null);
  const [apiRunEnabled, setApiRunEnabledState] = useState(flow.apiRunEnabled ?? false);

  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: getTags,
    staleTime: 30_000,
  });
  const tagColorByName = new Map(allTags.map(t => [t.name, t.color] as const));

  const suggestions = tagInput.trim().length > 0
    ? allTags.filter(t =>
        t.name.toLowerCase().includes(tagInput.trim().toLowerCase()) && !tags.includes(t.name),
      ).slice(0, 8)
    : [];

  function addTag(raw: string) {
    const t = raw.trim().toLowerCase();
    if (t.length === 0 || tags.includes(t)) {
      setTagInput('');
      return;
    }
    setTags([...tags, t]);
    setTagInput('');
  }

  function removeTag(t: string) {
    setTags(tags.filter(x => x !== t));
  }

  function handleTagKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && tagInput.length === 0 && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  }

  const mutation = useMutation({
    mutationFn: () => updateFlow(flow.id, {
      name: name.trim(),
      definition: flow.definition,
      batchConfig: flow.batchConfig ?? undefined,
      labelTemplate: labelTemplate.trim() === '' ? null : labelTemplate.trim(),
      tags,
      description: description.trim() === '' ? null : description.trim(),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flows', flow.id] });
      qc.invalidateQueries({ queryKey: ['flows'] });
      qc.invalidateQueries({ queryKey: ['tags'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Failed to save settings.');
    },
  });

  // Endpoint config like the webhook alias: saved on toggle, separately from the Save button.
  const apiRunMutation = useMutation({
    mutationFn: (enabled: boolean) => setApiRunEnabled(flow.id, enabled),
    onSuccess: res => {
      setApiRunEnabledState(res.apiRunEnabled);
      setError(null);
      qc.invalidateQueries({ queryKey: ['flows', flow.id] });
      qc.invalidateQueries({ queryKey: ['flows'] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Could not change the API/AI start setting.');
    },
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Flow settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="flow-name" className="text-xs text-muted-foreground">Name</Label>
            <Input
              id="flow-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="flow-desc" className="text-xs text-muted-foreground">
              Description <span className="text-muted-foreground/70">— shown in the flows list</span>
            </Label>
            <Textarea
              id="flow-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="What does this flow do?"
              className="resize-y"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Tags <span className="text-muted-foreground/70">— Enter or comma to add</span>
            </Label>
            <div className="border border-input rounded-md px-2 py-1.5 bg-background flex flex-wrap gap-1 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
              {tags.map(t => (
                <Chip key={t} label={t} color={tagColorByName.get(t)} onRemove={() => removeTag(t)} size="sm" />
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKey}
                onBlur={() => { if (tagInput.trim()) addTag(tagInput); }}
                placeholder={tags.length === 0 ? 'awork, prod, …' : ''}
                className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm"
              />
            </div>
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                <span className="text-xs text-muted-foreground self-center">existing:</span>
                {suggestions.map(t => (
                  <Chip key={t.id} label={t.name} color={t.color} onClick={() => addTag(t.name)} />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="flow-label-tpl" className="text-xs text-muted-foreground">
              Label template <span className="text-muted-foreground/70">— e.g. {'{{body.employee.name}}'}</span>
            </Label>
            <Input
              id="flow-label-tpl"
              type="text"
              value={labelTemplate}
              onChange={e => setLabelTemplate(e.target.value)}
              placeholder="Leave empty for auto preview"
              className="h-9 font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={apiRunEnabled}
                disabled={apiRunMutation.isPending}
                onChange={e => apiRunMutation.mutate(e.target.checked)}
              />
              <span>Allow starts via API / AI</span>
            </label>
            <p className="text-xs text-muted-foreground">
              When enabled, AI agents via MCP (<code>run_flow</code>) and API keys with the Editor
              role may start the published version of this flow. Saved immediately. Flows with a
              batch config cannot be started this way.
            </p>
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || name.trim().length === 0}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
